import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/Navigation';
import CosmicBackground from '../../components/CosmicBackground';
import ToolInterface from '../../components/ToolInterface';
import { getToolById } from '../../data/toolsData';

const EmailSequenceBuilderPage = () => {
  const tool = getToolById('email-sequence-builder');
  const [sequenceType, setSequenceType] = useState('welcome');
  const [businessType, setBusinessType] = useState('');
  const [goal, setGoal] = useState('');
  const [audience, setAudience] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState('');

  const sequenceTypes = [
    { id: 'welcome', name: 'Welcome Series' },
    { id: 'nurture', name: 'Nurture Sequence' },
    { id: 'sales', name: 'Sales Sequence' },
    { id: 'onboarding', name: 'Customer Onboarding' },
    { id: 'reengagement', name: 'Re-engagement Series' }
  ];

  const handleGenerate = async () => {
    if (!businessType.trim()) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      let generatedSequence = '';
      
      if (sequenceType === 'welcome') {
        generatedSequence = `🎯 WELCOME EMAIL SEQUENCE FOR ${businessType.toUpperCase()}

📧 EMAIL 1: IMMEDIATE WELCOME (Send immediately)
Subject: Welcome! Here's what happens next...

Hi [First Name]!

Welcome to the ${businessType} community! 🎉

I'm genuinely excited to have you here. When you signed up, you took the first step toward ${goal || 'achieving amazing results'}.

Here's what you can expect:
✅ Weekly insights delivered to your inbox
✅ Exclusive resources and tips
✅ A supportive community of ${audience || 'like-minded people'}

Your first gift is waiting: [Link to welcome bonus]

Talk soon!
[Your Name]

---

📧 EMAIL 2: YOUR STORY (Send Day 2)
Subject: Why I started this ${businessType}...

Hey [First Name],

Yesterday I welcomed you to our community, and today I want to share why this ${businessType} exists.

Three years ago, I was struggling with ${goal || 'the same challenges you might be facing'}. I felt overwhelmed, confused, and honestly, a bit lost.

That's when I discovered [your solution/method]. It changed everything.

Now, I've helped thousands of ${audience || 'people'} achieve ${goal || 'their goals'}, and I want to help you too.

Tomorrow, I'll share the #1 mistake most people make when starting out.

Best,
[Your Name]

---

📧 EMAIL 3: COMMON MISTAKE (Send Day 4)
Subject: The mistake that costs ${audience || 'people'} thousands...

Hi [First Name],

Ready for some tough love?

The biggest mistake I see ${audience || 'people'} make is trying to do everything at once.

They sign up for 10 courses, follow 20 gurus, and try 5 different strategies simultaneously.

Result? Overwhelm, confusion, and zero progress.

Here's what works instead: Pick ONE thing. Master it. Then move to the next.

In our next email, I'll show you exactly where to start.

Stay focused!
[Your Name]

---

📧 EMAIL 4: QUICK WIN (Send Day 7)
Subject: Your first quick win (takes 10 minutes)

Hey [First Name],

Ready for a quick win? Here's something you can do in the next 10 minutes that will make a real difference:

[Specific, actionable tip related to your niche]

This might seem simple, but it's the foundation everything else builds on.

Try it today and hit reply to let me know how it goes!

Cheering you on,
[Your Name]

---

📧 EMAIL 5: SOCIAL PROOF (Send Day 10)
Subject: [First Name], meet Sarah (she's just like you)

Hi [First Name],

I want you to meet Sarah.

Six months ago, Sarah was exactly where you are now - just starting her journey with ${businessType}.

She felt overwhelmed and wasn't sure if she could really ${goal || 'succeed'}.

Today? She's ${goal || 'achieved incredible results'} and couldn't be happier.

Here's what Sarah did differently: [Success story and key actions]

You can do this too. I believe in you.

[Your Name]

---

📧 EMAIL 6: VALUABLE RESOURCE (Send Day 14)
Subject: Your ${businessType} toolkit (free download)

Hey [First Name],

After two weeks together, I have a special gift for you.

I've created a comprehensive toolkit that includes:
• [Resource 1]
• [Resource 2]  
• [Resource 3]
• Bonus: [Special bonus]

Download it here: [Link]

This is my way of saying thank you for being part of our community.

Enjoy!
[Your Name]

💡 SEQUENCE NOTES:
• Total emails: 6
• Timeline: 2 weeks
• Focus: Building relationship and providing value
• Next step: Transition to regular newsletter or sales sequence`;

      } else if (sequenceType === 'sales') {
        generatedSequence = `💰 SALES EMAIL SEQUENCE FOR ${businessType.toUpperCase()}

📧 EMAIL 1: PROBLEM AWARENESS (Day 1)
Subject: The hidden problem holding ${audience || 'you'} back...

Hi [First Name],

Can I share something that might be uncomfortable to hear?

Most ${audience || 'people'} struggling with ${goal || 'their goals'} aren't failing because they lack talent or motivation.

They're failing because they don't realize they have a problem.

The problem? [Identify core issue your product solves]

Tomorrow, I'll show you exactly how this problem is costing you ${goal || 'results'}.

Talk soon,
[Your Name]

---

📧 EMAIL 2: COST OF INACTION (Day 3)
Subject: What this problem is really costing you...

Hey [First Name],

Yesterday, I mentioned the hidden problem most ${audience || 'people'} face.

Today, let's talk about what it's costing you:

💸 Financial cost: [Specific example]
⏰ Time cost: [Specific example]
😔 Emotional cost: [Specific example]
🚫 Opportunity cost: [Specific example]

The truth is, doing nothing isn't free. It's expensive.

In our next email, I'll show you there's a better way.

[Your Name]

---

📧 EMAIL 3: SOLUTION INTRODUCTION (Day 5)
Subject: There's a better way (and it's simpler than you think)

Hi [First Name],

Good news: You don't have to live with this problem forever.

There's a solution, and it's probably simpler than you think.

It's called [Your Product/Service].

Here's how it works: [Brief explanation of your solution]

The best part? You can start seeing results in [timeframe].

Want to learn more? [Soft CTA - link to sales page or more info]

Excited to share more,
[Your Name]

---

📧 EMAIL 4: PROOF & TESTIMONIALS (Day 7)
Subject: "This changed everything for me..." - Real results

Hey [First Name],

Don't just take my word for it. Here's what ${audience || 'people'} are saying about [Your Product]:

"[Testimonial 1 - specific results]" - Customer Name

"[Testimonial 2 - transformation story]" - Customer Name

"[Testimonial 3 - unexpected benefit]" - Customer Name

These aren't special cases. This is what happens when you have the right system.

Ready to get started? [Link to sales page]

[Your Name]

---

📧 EMAIL 5: OBJECTION HANDLING (Day 9)
Subject: "But what if it doesn't work for me?"

Hi [First Name],

I know what you're thinking: "This sounds great, but what if it doesn't work for me?"

Let me address the most common concerns:

❓ "I don't have enough time"
✅ Our system takes just [time commitment] per day

❓ "I'm not tech-savvy"  
✅ Everything is designed for beginners

❓ "I've tried other things before"
✅ This is different because [unique differentiator]

❓ "What if I don't get results?"
✅ 100% money-back guarantee

Your success is guaranteed. The only risk is not trying.

Get started today: [Link to sales page]

[Your Name]

---

📧 EMAIL 6: URGENCY & FINAL CALL (Day 12)
Subject: [First Name], this ends tomorrow...

Hey [First Name],

This is it - your final chance to join [Your Product] at this special price.

Tomorrow at midnight, this offer expires and the price goes up by $[amount].

Here's what you get when you join today:
✅ [Core product/service]
✅ [Bonus 1]
✅ [Bonus 2]
✅ [Guarantee]

Total value: $[total value]
Your price today: $[special price]

Don't let this opportunity slip away.

Secure your spot now: [Link to sales page]

Last call!
[Your Name]

💡 SEQUENCE NOTES:
• Total emails: 6
• Timeline: 12 days
• Focus: Problem → Solution → Proof → Close
• Include: Social proof, urgency, guarantees`;

      } else {
        // Default nurture sequence
        generatedSequence = `🌱 NURTURE EMAIL SEQUENCE FOR ${businessType.toUpperCase()}

📧 EMAIL 1: VALUE-FIRST APPROACH (Week 1)
Subject: The one thing that changes everything...

Hi [First Name],

I've been working with ${audience || 'people like you'} for years, and I've noticed something interesting.

The ones who succeed all have one thing in common: [Key insight or principle]

This week, I challenge you to try this: [Specific actionable advice]

It might seem small, but small changes create big results.

Let me know how it goes!
[Your Name]

---

📧 EMAIL 2: EDUCATIONAL CONTENT (Week 2)  
Subject: The 3-step framework that actually works

Hey [First Name],

Last week, you tried [previous advice]. How did it go?

Today, I want to share a simple 3-step framework that's helped thousands of ${audience || 'people'} ${goal || 'achieve their goals'}:

Step 1: [First step with explanation]
Step 2: [Second step with explanation]  
Step 3: [Third step with explanation]

The key is consistency. Small daily actions compound over time.

Which step will you focus on this week?

[Your Name]

---

📧 EMAIL 3: BEHIND THE SCENES (Week 3)
Subject: What I learned from my biggest failure...

Hi [First Name],

Story time.

Five years ago, I made a massive mistake that cost me [specific consequence].

Here's what happened: [Brief story of failure]

But here's what I learned: [Key lesson]

That failure taught me [insight that helps your audience].

Sometimes our biggest setbacks become our greatest comebacks.

What's a failure that taught you something valuable?

[Your Name]

---

📧 EMAIL 4: COMMUNITY HIGHLIGHT (Week 4)
Subject: This will inspire you (real success story)

Hey [First Name],

I love sharing success stories from our community, and this one gave me chills.

Meet [Customer Name]. When she started, she was [starting situation].

In just [timeframe], she [specific achievement].

Here's exactly what she did: [Key actions taken]

The best part? She said: "[Quote about transformation]"

You have the same potential. What's one small step you can take today?

Believing in you,
[Your Name]

💡 SEQUENCE NOTES:
• Total emails: 4+ (can continue weekly)
• Timeline: Monthly nurture cycle
• Focus: Value, education, inspiration, community
• Goal: Build trust and stay top-of-mind`;
      }
      
      setResult(generatedSequence);
      setIsGenerating(false);
    }, 3000);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  const handleDownload = () => {
    const blob = new Blob([result], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sequenceType}-email-sequence.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />
      
      <section className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ToolInterface
              tool={tool}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              result={result}
              onCopy={handleCopy}
              onDownload={handleDownload}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Business Type *
                  </label>
                  <input
                    type="text"
                    value={businessType}
                    onChange={(e) => setBusinessType(e.target.value)}
                    placeholder="e.g., online coaching, digital marketing agency, course creation"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Sequence Type
                  </label>
                  <select
                    value={sequenceType}
                    onChange={(e) => setSequenceType(e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  >
                    {sequenceTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g., busy entrepreneurs, new coaches, small business owners"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Primary Goal
                  </label>
                  <input
                    type="text"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="e.g., build their business, lose weight, learn new skills"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>
            </ToolInterface>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EmailSequenceBuilderPage;